import ShapeBase from './ShapeBase';
import tools from '../tools';
import { fas, faIcons } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import type Shape from '../objects/Shape';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { type IconData } from '../types';

class ShapeIcon extends ShapeBase {
  static iconClass() {
    return 'fa-regular fa-icons';
  }

  iconClass() {
    const icon = this.iconData();
    return `${icon.prefix} fa-${icon.iconName}`;
  }

  constructor(fabricObject: Shape) {
    super(fabricObject);

    this.fabricObject.uniformScaling = true;

    this.fabricObject.set('width', 100);
    this.setHeightFromIcon();
  }

  hasEmbeddedText() {
    return false;
  }

  hasFixedDimensions() {
    return false;
  }

  tools() {
    return [
      [tools.ToolChooseIcon],
      [tools.ToolFillColor, tools.ToolBorderColor, tools.ToolStrokeStyle],
    ];
  }

  // TODO: incorporate icon height/width so that connectors come right to edge.

  static tokenizeIconString(str: string) {
    if (typeof str === 'number') {
      return [];
    } else {
      return str.split(/[^a-z]/);
    }
  }

  static _iconDB: Record<string, IconData>;

  static get iconDB() {
    if (!this._iconDB) {
      this._iconDB = {};
      for (const family of [fas, far]) {
        Object.values(family).forEach(icon => {
          const synonyms = icon.icon[2].map(s => this.tokenizeIconString(s));

          this._iconDB[`${icon.prefix}/${icon.iconName}`] = {
            ...icon,
            synonyms: [this.tokenizeIconString(icon.iconName), synonyms].flat(
              10
            ),
          };
        });
      }

      // Stuff in our custom icon. If we add any more custom icons here we should move this into another file.
      this._iconDB['custom/aha'] = {
        prefix: 'custom',
        iconName: 'aha',
        synonyms: ['aha', 'logo'],
        icon: [
          347,
          130,
          [],
          'a',
          `
          M65.3557 76.4153C62.9961 66.4745 59.1605 48.9294 55.735 33.7145C53.3442 44.2676 47.5218 66.0869 44.7845 76.4153H65.3557ZM38.6384 100.622L32.3713 128.214C32.1878 129.008 31.4804 129.576 30.6759 129.576L1.72851 129.426C1.17337 129.415 0.655408 129.147 0.325683 128.695C0.00794895 128.238 -0.0891698 127.651 0.0870828 127.119L38.2608 8.9635C38.2608 8.9635 38.8818 6.74013 41.6132 6.2335C50.0637 4.682 63.4301 2.52546 72.8542 0.835458C74.9033 0.462467 75.8541 2.44041 75.8541 2.44041L113.716 127.939C113.838 128.357 113.765 128.799 113.509 129.138C113.252 129.486 112.856 129.695 112.436 129.685L95.5477 129.535C91.073 129.535 82.3419 129.819 78.9092 129.933C78.0735 129.962 77.3421 129.386 77.1586 128.561L70.9898 100.622H38.6384Z
          M156.247 47.3092C177.931 32.8207 195.957 36.0463 204.817 44.6167C215.551 55.3258 209.279 69.2593 212.95 127.668C212.976 128.16 212.798 128.642 212.447 128.99C212.091 129.348 211.6 129.541 211.089 129.531C207.877 129.453 201.548 129.308 198.391 129.308C193.114 129.308 182.853 129.729 178.62 129.915C178.122 129.938 177.637 129.761 177.281 129.422C176.917 129.085 176.72 128.633 176.72 128.149C177.153 70.8028 177.574 63.1093 166.828 63.1093C163.188 63.1093 159.892 64.614 156.768 66.1345C156.94 95.789 157.381 104.193 158.375 128.111C158.388 128.493 158.247 128.866 157.979 129.134C157.706 129.408 157.33 129.565 156.94 129.576L122.883 129.999C122.488 130.014 122.119 129.864 121.845 129.591C121.565 129.332 121.417 128.97 121.417 128.593C122.208 42.9869 121.571 25.2899 118.881 7.3779C118.639 5.77361 120.321 5.58901 120.321 5.58901C135.416 3.40299 136.855 3.19168 154.372 8.69751e-05C154.372 8.69751e-05 156.247 -0.0448478 156.247 1.908V47.3092Z
          M266.458 86.8793C258.319 87.5318 246.024 88.3383 246.024 98.19C246.024 106.699 256.092 109.555 266.458 103.519V86.8793ZM266.974 119.663C260.763 124.008 248.227 130 238.362 130C222.846 130 212.952 119.555 212.952 103.567C212.952 79.0677 243.606 72.0288 265.765 69.2814C266.108 65.2396 266.287 60.8851 255.029 60.8851C243.932 60.8851 234.639 64.0503 225.177 67.5645C224.604 67.7805 223.954 67.7081 223.452 67.3766C222.948 67.0382 222.67 66.4861 222.695 65.9177L223.7 47.1025C223.745 46.3894 224.259 45.7848 224.984 45.5724C244.758 39.9935 278.435 35.6121 292.772 47.8028C301.473 55.1988 301.047 64.6 300.741 80.4216C300.301 103.272 300.652 109.597 302.09 126.625C302.128 127.088 301.956 127.547 301.619 127.89C301.289 128.234 300.804 128.434 300.309 128.443L268.851 128.877C268.373 128.883 267.903 128.711 267.56 128.401C267.216 128.077 267.018 127.652 267.018 127.198L266.974 119.663Z
          M314.393 84.9999C313.358 85.0118 312.489 84.2855 312.442 83.3534C310.354 39.4826 309.815 34.6848 307.055 7.30274C306.968 6.40627 307.662 5.60267 308.643 5.45147L344.436 0.0262595C345.021 -0.0689824 345.621 0.0988814 346.058 0.464372C346.496 0.825101 346.719 1.34536 346.657 1.88586C344.571 21.5926 342.228 58.7619 341.789 82.795C341.771 83.7212 340.949 84.4713 339.918 84.4903L314.393 84.9999Z
          M346.33 127.854C346.357 128.357 346.156 128.841 345.798 129.191C345.433 129.547 344.945 129.736 344.433 129.72C332.237 129.236 324.395 129.282 313.951 129.996C313.438 130.031 312.939 129.852 312.567 129.512C312.193 129.155 311.989 128.678 312 128.169C312.181 118.48 312.225 108.118 312.13 102.113C312.116 101.605 312.335 101.111 312.707 100.771C313.079 100.425 313.584 100.252 314.099 100.292C322.607 100.949 337.841 100.736 344.651 100.012C345.175 99.9502 345.703 100.124 346.093 100.481C346.483 100.847 346.69 101.354 346.665 101.87C346.228 111.925 346.061 120.61 346.33 127.854Z
          `,
        ],
      };
    }

    return this._iconDB;
  }

  wasSet(keyOrPath: KeyPath, value: unknown) {
    super.wasSet(keyOrPath, value);

    if (keyOrPath === 'iconName') {
      this.setHeightFromIcon();
    }
  }

  setHeightFromIcon() {
    // Set the height of the icon appropriately.
    const icon = this.iconData().icon;
    const iconW = icon[0];
    const iconH = icon[1];
    this.fabricObject.set('height', (this.fabricObject.width * iconH) / iconW);
  }

  iconData() {
    return ShapeIcon.iconDB[this.fabricObject.iconName!] || faIcons;
  }

  static getPath(
    ctx: CanvasRenderingContext2D,
    iconPath: string | string[]
  ): Path2D {
    // @ts-expect-error createPath will exist when rendering to SVG.
    if (ctx.createPath) {
      // @ts-expect-error createPath will exist when rendering to SVG.
      return ctx.createPath(iconPath);
    }

    // assume this is a string, not a string[]
    return new Path2D(iconPath as string);
  }

  render(ctx: CanvasRenderingContext2D) {
    const icon = this.iconData().icon;

    const obj = this.fabricObject;
    const w = obj.width;
    const h = obj.height;

    // Render a font-awesome icon.
    const iconW = icon[0];
    const iconPath = icon[4];

    ctx.save();

    ctx.translate(-w / 2, -h / 2);
    ctx.scale(w / iconW, w / iconW);

    const path = ShapeIcon.getPath(ctx, iconPath);

    // Copied from _renderPaintInOrder.
    if (obj.fill) {
      obj._setFillStyles(ctx, obj);
      ctx.fill(path);
    }
    if (obj.stroke && obj.strokeWidth > 0) {
      this._setBorderDash(ctx);
      obj._setStrokeStyles(ctx, obj);
      ctx.lineWidth = (obj.strokeWidth * iconW) / w / obj.scaleX;
      ctx.stroke(path);
    }
    ctx.restore();
  }
}

export default ShapeIcon;
